import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { User, Users, Target } from "lucide-react";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "./components/BasicAdvancedWrapper";
import { MultiStepConfig } from "./components/MultiStepGradeModal";
import { CreateModalProps } from "./index";
import {
  TeacherRoleStep,
  StudentSkillsStep,
  TargetSkillsEvaluationStep,
} from "./collaborative-group-work/steps";
import CollaborativeSpecificFields from "./collaborative-group-work/CollaborativeSpecificFields";
import {
  CollaborativeGroupWorkCreateRequestDto,
  CollaborativeGroupWorkCreateRequestDtoCourseName,
  CollaborativeGroupWorkCreateRequestDtoContentLanguage,
  CollaborativeGroupWorkCreateRequestDtoGenerationType,
} from "@packages/api/madlenAPI.schemas";

interface CollaborativeCreateModalProps
  extends Omit<CreateModalProps, "onSubmit" | "themeColor"> {
  onSubmit: (input: any) => Promise<void>;
  themeColor?: string; // Optional since it's hardcoded to "amber"
}

export const CollaborativeCreateModal: React.FC<
  CollaborativeCreateModalProps
> = ({ onSubmit, initialInput, ...props }) => {
  const { t } = useTranslation();

  // State for basic collaborative fields
  const [basicStudentsPerGroup, setBasicStudentsPerGroup] = useState(
    (initialInput as any).studentsPerGroup || 3,
  );

  // Define the initial data structure for advanced mode
  const initialAdvancedData: CollaborativeGroupWorkCreateRequestDto = {
    // Basic CreateModal data (will be populated from first step)
    courseName:
      initialInput.courseName as CollaborativeGroupWorkCreateRequestDtoCourseName,
    gradeLevel: initialInput.gradeLevel,
    prompt: initialInput.prompt,
    environment: initialInput.environment,
    isPublic: initialInput.isPublic,
    generationType:
      initialInput.generationType as CollaborativeGroupWorkCreateRequestDtoGenerationType,
    teacherDocumentIds: initialInput.teacherDocumentIds || [],
    learningOutcomeIds: initialInput.learningOutcomeIds || [],
    contentLanguage:
      initialInput.outputLanguage as CollaborativeGroupWorkCreateRequestDtoContentLanguage,

    // Required field
    studentsPerGroup: basicStudentsPerGroup,

    // Advanced collaborative-specific defaults
    teacherRole: "facilitator",
    classroomEnvironment: "face_to_face",
    physicalLayout: "flexible_seating",
    availableMaterials: ["textbooks", "whiteboard", "digital_devices"],
    studentSocialSkills: "intermediate",
    previousCollaborationExperience: "moderate",
    targetSkills: [
      "communication",
      "cooperation",
      "critical_thinking",
      "problem_solving",
    ],
    evaluationTypes: [
      "peer_evaluation",
      "self_reflection",
      "teacher_observation",
    ],
    interactionStyle: "fixed_roles",
    sessionDuration: "40_minutes",
  };

  // Define the advanced steps configuration
  const advancedSteps: MultiStepConfig<CollaborativeGroupWorkCreateRequestDto>[] =
    [
      {
        id: "teacher-role",
        title: t(
          "collaborative.steps.teacherRole.title",
          "Teacher Role & Environment",
        ),
        description: t(
          "collaborative.steps.teacherRole.description",
          "Define your role and the classroom environment",
        ),
        icon: <User size={16} />,
        component: TeacherRoleStep,
        validation: (data) => {
          // This step is optional, so always valid
          return true;
        },
      },
      {
        id: "student-skills",
        title: t(
          "collaborative.steps.studentSkills.title",
          "Student Skills & Materials",
        ),
        description: t(
          "collaborative.steps.studentSkills.description",
          "Configure student abilities and available resources",
        ),
        icon: <Users size={16} />,
        component: StudentSkillsStep,
        validation: (data) => {
          // This step is optional, so always valid
          return true;
        },
      },
      {
        id: "target-skills-evaluation",
        title: t(
          "collaborative.steps.targetSkills.title",
          "Target Skills & Evaluation",
        ),
        description: t(
          "collaborative.steps.targetSkills.description",
          "Define skills to develop and evaluation methods",
        ),
        icon: <Target size={16} />,
        component: TargetSkillsEvaluationStep,
        validation: (data) => {
          // This step is optional, so always valid
          return true;
        },
      },
    ];

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    let submissionData = {
      ...data,
      studentsPerGroup: basicStudentsPerGroup,
    };

    await onSubmit(submissionData);
  };

  // Handle advanced mode submission
  const handleAdvancedSubmit = async (
    data: CollaborativeGroupWorkCreateRequestDto,
  ) => {
    let submissionData = { ...data };

    // Construct enriched prompt based on advanced settings
    let enrichedPrompt = data.prompt || "";

    // Add teacher role
    if (data.teacherRole) {
      enrichedPrompt += `\n\nTeacher Role: ${data.teacherRole}`;
    }

    // Add classroom environment
    if (data.classroomEnvironment) {
      enrichedPrompt += `\nClassroom Environment: ${data.classroomEnvironment}`;
    }

    // Add physical layout
    if (data.physicalLayout) {
      enrichedPrompt += `\nPhysical Layout: ${data.physicalLayout}`;
    }

    // Add available materials
    if (data.availableMaterials && data.availableMaterials.length > 0) {
      enrichedPrompt += `\nAvailable Materials: ${data.availableMaterials.join(", ")}`;
    }

    // Add student social skills
    if (data.studentSocialSkills) {
      enrichedPrompt += `\nStudent Social Skills: ${data.studentSocialSkills}`;
    }

    // Add previous collaboration experience
    if (data.previousCollaborationExperience) {
      enrichedPrompt += `\nPrevious Collaboration Experience: ${data.previousCollaborationExperience}`;
    }

    // Add target skills
    if (data.targetSkills && data.targetSkills.length > 0) {
      enrichedPrompt += `\nTarget Skills: ${data.targetSkills.join(", ")}`;
    }

    // Add evaluation types
    if (data.evaluationTypes && data.evaluationTypes.length > 0) {
      enrichedPrompt += `\nEvaluation Types: ${data.evaluationTypes.join(", ")}`;
    }

    // Add interaction style
    if (data.interactionStyle) {
      enrichedPrompt += `\nInteraction Style: ${data.interactionStyle}`;
    }

    // Add session duration
    if (data.sessionDuration) {
      enrichedPrompt += `\nSession Duration: ${data.sessionDuration}`;
    }

    submissionData.prompt = enrichedPrompt;

    await onSubmit(submissionData);
  };

  // Handle students per group change from basic mode
  const handleStudentsPerGroupChange = (studentsPerGroup: number) => {
    setBasicStudentsPerGroup(studentsPerGroup);
  };

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<CollaborativeGroupWorkCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "amber",
        onSubmit: handleBasicSubmit,
        after: (
          <CollaborativeSpecificFields
            studentsPerGroup={basicStudentsPerGroup}
            onChange={handleStudentsPerGroupChange}
            themeColor="amber"
          />
        ),
      },
      multiStepProps: {
        steps: advancedSteps,
        initialData: initialAdvancedData,
        title: t(
          "quickActions.collaborativeCreate",
          "Create Collaborative Group Work",
        ),
        themeColor: "amber",
        backPath: "/content/collaborative",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: handleAdvancedSubmit,
        basicModeSubmitText: t("common.generate", "Generate"),
        advancedModeSubmitText: t("collaborative.createAdvanced"),
      },
      themeColor: "amber",
      title: t(
        "quickActions.collaborativeCreate",
        "Create Collaborative Group Work",
      ),
      backPath: "/content/collaborative",
      enableAdvancedMode: true,
      defaultMode: "basic",
      showModeToggle: true,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: handleAdvancedSubmit,
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default CollaborativeCreateModal;
